/**
 * MUTAGIM.SHOP — Admin Search Analytics Page (Phase 7)
 * Analytics des recherches et termes sans résultats
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  ArrowLeft, Search, TrendingUp, AlertCircle, RefreshCw, Calendar,
  BarChart3, Eye, Package
} from 'lucide-react';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminSearchAnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [analytics, setAnalytics] = useState(null);
  const [noResults, setNoResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7');
  
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);
  
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const [analyticsRes, noResultsRes] = await Promise.all([
        axios.get(`${API_URL}/api/search/admin/analytics`, { 
          headers, 
          params: { days: parseInt(period) }
        }),
        axios.get(`${API_URL}/api/search/admin/no-results`, { 
          headers, 
          params: { limit: 50 }
        })
      ]);
      
      setAnalytics(analyticsRes.data);
      setNoResults(noResultsRes.data.no_results || []);
      
    } catch (err) {
      console.error('Failed to load analytics:', err);
      toast.error('Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, period]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminPageWrapper title="Analytics Recherche" subtitle="Termes recherchés et opportunités">
      <div className="space-y-6" data-testid="admin-search-analytics-page">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-mutagim-red" />
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total recherches</p>
                      <p className="text-3xl font-bold">{analytics?.total_searches || 0}</p>
                    </div>
                    <div className="p-3 rounded-full bg-blue-100">
                      <Search className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Avec résultats</p>
                      <p className="text-3xl font-bold text-green-600">
                        {analytics?.searches_with_results || 0}
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-green-100">
                      <Package className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Sans résultats</p>
                      <p className="text-3xl font-bold text-red-600">
                        {analytics?.searches_no_results || 0}
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-red-100">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Taux de succès</p>
                      <p className="text-3xl font-bold">
                        {analytics?.success_rate || 0}%
                      </p>
                    </div>
                    <div className="p-3 rounded-full bg-purple-100">
                      <BarChart3 className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Recherches par image */}
            {analytics?.image_searches > 0 && (
              <Card className="bg-gradient-to-r from-purple-50 to-blue-50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-white shadow">
                      <Eye className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Recherches par image (IA)</p>
                      <p className="text-2xl font-bold text-purple-700">
                        {analytics.image_searches}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Onglets */}
            <Tabs defaultValue="no-results" className="space-y-4">
              <TabsList>
                <TabsTrigger value="no-results" className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Termes sans résultats
                  {noResults.length > 0 && (
                    <Badge variant="destructive" className="ml-1">{noResults.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="top-terms" className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Top recherches
                </TabsTrigger>
              </TabsList>
              
              {/* Termes sans résultats */}
              <TabsContent value="no-results">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="w-5 h-5" />
                      Termes sans résultats
                    </CardTitle>
                    <CardDescription>
                      Ces termes ont été recherchés mais n'ont trouvé aucun produit. 
                      Utile pour savoir quels produits importer !
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      {noResults.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">
                          Aucune recherche sans résultat sur cette période
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {noResults.map((item, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <Badge variant="outline" className="font-mono">
                                  {item.search_count}x
                                </Badge>
                                <span className="font-medium text-gray-800">
                                  "{item.query}"
                                </span>
                              </div>
                              <span className="text-sm text-gray-500">
                                {formatDate(item.last_searched)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Top recherches */}
              <TabsContent value="top-terms">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-700">
                      <TrendingUp className="w-5 h-5" />
                      Top 10 recherches
                    </CardTitle>
                    <CardDescription>
                      Les termes les plus recherchés sur cette période
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!analytics?.top_terms || analytics.top_terms.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        Aucune recherche sur cette période
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {analytics.top_terms.map((item, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center gap-4"
                          >
                            <div className={`
                              w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                              ${idx === 0 ? 'bg-yellow-100 text-yellow-700' : 
                                idx === 1 ? 'bg-gray-200 text-gray-700' :
                                idx === 2 ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-600'}
                            `}>
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <span className="font-medium">"{item.query}"</span>
                            </div>
                            <Badge variant="secondary">
                              {item.count} recherches
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </AdminPageWrapper>
  );
}

export default AdminSearchAnalyticsPage;
